import Box from "@mui/material/Box";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { Button, TextField, Toolbar, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

export interface DataGridProps {
  rows: [];
  columns: CustomGridColDef[];
  checkboxSelection: boolean;
  title: string;
  action: () => void;
  actionTitle: string;
  actionSearch?: (search: string) => void | null | undefined | Promise<void>;
  actionSearchTitle?: string | null | undefined;
  hiddenSearchButton?: boolean;
  onRowClick?: (row: any) => void;
  height?: number;
  loading?: any;
  sx?: any;
  style?: React.CSSProperties;
}

export type CustomGridColDef = GridColDef & {
  importance?: number;
};

export const DataGridCustom: React.FC<DataGridProps> = ({ rows, columns, checkboxSelection, title, action, actionTitle, actionSearch, actionSearchTitle, hiddenSearchButton = false, onRowClick }) => {
  const theme = useTheme();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  let filteredColumns = columns;
  filteredColumns = columns.filter((col) => (col.importance ?? 0) < screenWidth); // Only the columns with importance < screenWidth will be shown

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [pageSize, setPageSize] = useState(0);
  const rowHeight = 52;
  const headerHeight = 56;

  useEffect(() => {
    const calculatePageSize = () => {
      const availableHeight = window.innerHeight - 300;
      let visibleRows = Math.floor(availableHeight / rowHeight);
      if (visibleRows == 0) {
        visibleRows = 1;
      }
      setPageSize(visibleRows);
      setPaginationModel((prevModel) => ({
        ...prevModel,
        pageSize: visibleRows,
      }));
    };
    calculatePageSize();
    window.addEventListener("resize", calculatePageSize); // Set Page Size when window resizes
    return () => window.removeEventListener("resize", calculatePageSize);
  }, []);

  const [searchText, setSearchText] = useState("");
  const [filteredRows, setFilteredRows] = useState(rows);
  useEffect(() => {
    if (rows?.length > 0) {
      const searchValue = "";
      const simulatedEvent = {
        target: { value: searchValue },
      } as React.ChangeEvent<HTMLInputElement>;
      setFilteredRows(rows as any);
    }
  }, [rows]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    if (!hiddenSearchButton) {
      const filtered = rows.filter((row) => Object.values(row).some((field) => String(field).toLowerCase().includes(value)));
      setFilteredRows(filtered as any);
    } else {
      setFilteredRows(rows);
    }
  };

  const Search = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    const filtered = rows.filter((row) => Object.values(row).some((field) => String(field).toLowerCase().includes(value)));
    setFilteredRows(filtered as any);
  };

  return (
    <Box
      sx={{
        height: "90%",
        width: "100%",
        "& .MuiToolbar-root": {
          paddingBottom: 3,
          paddingTop: 1,
        },
        "& .MuiToolbar-gutters": {
          paddingLeft: 0,
          paddingRight: 1,
        },
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", borderRadius: 1 }}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            flex: 2,
            maxWidth: "40%",
            width: "50%",
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            width: "80%",
            justifyContent: "flex-end",
          }}
        >
          <TextField
            label="Cerca"
            onChange={handleSearch}
            size="small"
            variant="outlined"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                actionSearch!(searchText);
              }
            }}
            sx={{
              width: { xs: "30%", sm: "40%", md: "60%" },
              backgroundColor: theme.palette.mode === "dark" ? "rgb(18,20,24)" : "rgb(255,255,255)",
              borderRadius: 2,
              border: "none",
              "& .MuiFormLabel-root": {
                color: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)", // Default color
                "&.Mui-focused": {
                  color: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)", // Override focused color
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)", // Default color
                "&.Mui-focused": {
                  color: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)", // Override focused color
                },
              },
              "& .MuiOutlinedInput-root": {
                boxShadow: "none",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)", // Override border color
                },
                "& fieldset": {
                  border: "none",
                },
              },
              "& .MuiOutlinedInput-input": {
                boxShadow: "none",
              },
            }}
          />
          {hiddenSearchButton && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.mode === "dark" ? "rgb(98, 98, 98)" : "rgb(255,255,255)",
                color: theme.palette.mode === "dark" ? "#FFFFFF" : "#000000",
              }}
              onClick={() => {
                actionSearch!(searchText);
              }}
            >
              Cerca{""}
            </Button>
          )}

          <Button
            sx={{
              backgroundColor: "rgb(190,209,113)",
              "&:hover": {
                color: "rgb(0,0,0)",
              },
            }}
            variant="contained"
            onClick={action}
          >
            {actionTitle}
          </Button>
        </Box>
      </Toolbar>

      <DataGrid
        rows={hiddenSearchButton ? rows : filteredRows}
        columns={filteredColumns as any}
        disableColumnSelector
        onRowClick={onRowClick}
        sx={{
          "& .MuiDataGrid-footerButton": {
            borderRadius: 4,
          },
          "& .MuiDataGrid-columnMenuItem": {
            borderRadius: 4,
          },
          "& .MuiDataGrid-toolbar": {
            borderRadius: 4,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          backgroundColor: theme.palette.mode === "dark" ? "#111418" : "#FFFFFF",
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
        pageSizeOptions={[pageSize]}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        rowHeight={rowHeight}
        localeText={{
          noRowsLabel:
            document.title === "Nanesa - Spedizioni" ? "Premere cerca per visualizzare le spedizioni"
            : document.title === "Nanesa - Utenti" ? "Nessun utente presente"
            : "Nessun dato disponibile", // Default fallback
        }}
      />
    </Box>
  );
};
